import { useState } from "react";
import { useSearchParams } from "react-router";
import { CommunicationChannel } from "@shared/schemas/communication-channel.schemas";
import { EventIdPublicOrSlug } from "@shared/schemas/branded-id.schemas";
import { ThemeMode } from "@shared/types/theme-mode.types";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { i18n } from "@frontend/i18n";
import { useAnonymousActions } from "@frontend/shared/features/actions/application/actions/use-anonymous-actions";
import { resolvePublicAssetUrl } from "@frontend/shared/infrastructure/assets/resolve-public-asset-url";
import { useEventTheme } from "@/shared/presentation/hooks/useEventTheme";

interface EventLandingControllerReturn {
  readonly state: {
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly eventName: string;
    readonly logoUrl: string | null;
    readonly keyVisualSrc: string | null;
    readonly sponsorLogoSrc: string | null;
    readonly sponsorName: string;
    readonly participantCount: number | null;
    readonly isTvMode: boolean;
    readonly tvHref: string;
    readonly exitTvHref: string;
    readonly whatsappUrl: string | null;
    readonly termsHref: string;
    readonly privacyHref: string;
    readonly isQrExpanded: boolean;
  };
  readonly actions: {
    readonly toggleQrExpanded: () => void;
  };
}

export function useEventLandingController(
  eventIdPublicOrSlug: string,
): EventLandingControllerReturn {
  useEventTheme(ThemeMode.LIGHT);
  const [isQrExpanded, setIsQrExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const inv = searchParams.get("inv") ?? undefined;
  const isTvMode = searchParams.has("tv");
  // Hrefs that toggle TV mode while preserving ?inv= and friends.
  const tvParams = new URLSearchParams(searchParams);
  tvParams.set("tv", "1");
  const tvHref = `?${tvParams.toString()}`;
  const exitParams = new URLSearchParams(searchParams);
  exitParams.delete("tv");
  const exitTvHref = `?${exitParams.toString()}`;
  const { useGetEventPublicView, useGetEventJoinInfo } = useAnonymousActions();
  const { data, isLoading, error } = useGetEventPublicView({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(eventIdPublicOrSlug),
  });
  const { data: joinInfo, isLoading: isEventJoinInfoLoading } =
    useGetEventJoinInfo(eventIdPublicOrSlug, {
      inv,
      lang: i18n.getLocale(),
    });

  const toggleQrExpanded = (): void => {
    setIsQrExpanded((prev) => !prev);
  };

  const placeholderResult = (flags: {
    readonly isLoading: boolean;
    readonly isError: boolean;
  }): EventLandingControllerReturn => ({
    state: {
      isLoading: flags.isLoading,
      isError: flags.isError,
      eventName: "",
      logoUrl: null,
      keyVisualSrc: null,
      sponsorLogoSrc: null,
      sponsorName: "",
      participantCount: null,
      isTvMode,
      tvHref,
      exitTvHref,
      whatsappUrl: null,
      termsHref: "",
      privacyHref: "",
      isQrExpanded,
    },
    actions: { toggleQrExpanded },
  });

  // Gate render on the join-info query too: otherwise the WhatsApp CTA/QR
  // render with the fallback phrase before resolution, and an immediate tap
  // would send the non-deterministic default instead of the event trigger.
  if (isLoading || isEventJoinInfoLoading) {
    return placeholderResult({ isLoading: true, isError: false });
  }

  if (error !== null || data === undefined) {
    return placeholderResult({ isLoading: false, isError: true });
  }

  const waChannel = data.allowedCommunicationChannels.find(
    (c) => c.channel === CommunicationChannel.WHATSAPP,
  );
  const encodedMessage =
    joinInfo?.markerEncodedPhrase ??
    encodeURIComponent(i18n.t("user.eventLanding.whatsapp.defaultMessage"));
  const whatsappUrl =
    waChannel !== undefined
      ? `https://wa.me/${waChannel.identifier}?text=${encodedMessage}`
      : null;

  // Isolated events used to link the event-hub policies, but those pages
  // 404 on planta.co — link the live documents until the hub pages exist.
  const termsHref = UserAppRoutes.terms();
  const privacyHref = UserAppRoutes.privacy();

  return {
    state: {
      isLoading: false,
      isError: false,
      eventName: LocalizedTextUtils.resolve(data.name, i18n.getLocale()),
      logoUrl: resolvePublicAssetUrl(data.logoUrl) ?? null,
      keyVisualSrc: resolvePublicAssetUrl(data.keyVisualUrl) ?? null,
      sponsorLogoSrc: resolvePublicAssetUrl(data.sponsorLogoUrl) ?? null,
      sponsorName: data.sponsorName ?? "",
      participantCount: joinInfo?.participantCount ?? null,
      isTvMode,
      tvHref,
      exitTvHref,
      whatsappUrl,
      termsHref,
      privacyHref,
      isQrExpanded,
    },
    actions: { toggleQrExpanded },
  };
}
